import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import type { ShopSummary } from '../../api/types.ts';
import { useAuth } from '../../auth/AuthContext.tsx';
import { EmptyRow } from '../../components/EmptyRow.tsx';
import { LoadErrorAlert } from '../../components/LoadErrorAlert.tsx';
import { LoadingRow } from '../../components/LoadingRow.tsx';
import { QuietButton } from '../../components/QuietButton.tsx';
import { useCompact } from '../../hooks/useCompact.ts';
import { useLoadState } from '../../hooks/useLoadState.ts';
import { shopDetailPath } from '../../routing/routes.ts';
import { formatLocalDateTime } from '../dashboard/dashboardHelpers.ts';
import { ShopFormDialog } from './ShopFormDialog.tsx';
import { filterShops, ownerOptionsFromShops, type ShopFilterState } from './shopsFilterHelpers.ts';
import { ownersDisplayText, SHOP_STATUS_OPTIONS, SHOP_TYPE_OPTIONS, statusChipColor, statusLabel } from './shopsHelpers.ts';

const NO_FILTER: ShopFilterState = { type: '', status: '', ownerId: '' };

interface FilterSelectProps {
  label: string;
  ariaLabel: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
}

// Local to ShopsPage -- dedups the three Type/Status/Owner filter dropdowns.
function FilterSelect({ label, ariaLabel, value, onChange, options }: Readonly<FilterSelectProps>): React.JSX.Element {
  const labelId = `shops-${ariaLabel}-label`;
  return (
    <FormControl size="small" sx={{ minWidth: 150 }}>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        inputProps={{ 'aria-label': ariaLabel }}
      >
        <MenuItem value="">All</MenuItem>
        {options.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export function ShopsPage(): React.JSX.Element {
  const { api, hasPermission } = useAuth();
  const navigate = useNavigate();
  // Phone-width (<sm): hide Type/Owners/Last licence expiry so the table stops clipping.
  const compact = useCompact();
  const canWrite = hasPermission('shops:write');

  const [includeArchived, setIncludeArchived] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filter, setFilter] = useState<ShopFilterState>(NO_FILTER);

  const fetchShops = useCallback(async () => (await api.shops.list(includeArchived)).shops, [api, includeArchived]);
  const { data: shops, state } = useLoadState(fetchShops, [] as ShopSummary[]);

  const ownerOptions = ownerOptionsFromShops(shops);
  const visibleShops = filterShops(shops, filter);
  const filterActive = filter.type !== '' || filter.status !== '' || filter.ownerId !== '' || includeArchived;
  const colSpan = 7 - (compact ? 3 : 0); // compact hides Type, Owners, Last licence expiry

  const clearFilters = () => {
    setFilter(NO_FILTER);
    setIncludeArchived(false);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5">Shops</Typography>
        {canWrite && <Button onClick={() => setDialogOpen(true)}>Add shop</Button>}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <FilterSelect
          label="Type"
          ariaLabel="filter-type"
          value={filter.type}
          onChange={(value) => setFilter((f) => ({ ...f, type: value as ShopFilterState['type'] }))}
          options={SHOP_TYPE_OPTIONS}
        />
        <FilterSelect
          label="Status"
          ariaLabel="filter-status"
          value={filter.status}
          onChange={(value) => setFilter((f) => ({ ...f, status: value as ShopFilterState['status'] }))}
          options={SHOP_STATUS_OPTIONS}
        />
        <FilterSelect
          label="Owner"
          ariaLabel="filter-owner"
          value={filter.ownerId}
          onChange={(value) => setFilter((f) => ({ ...f, ownerId: value }))}
          options={ownerOptions.map((o) => ({ value: String(o.id), label: o.label }))}
        />
        <FormControlLabel
          control={
            <Switch
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              slotProps={{ input: { 'aria-label': 'show-archived' } }}
            />
          }
          label="Show archived"
        />
        <QuietButton onClick={clearFilters} disabled={!filterActive}>
          Clear
        </QuietButton>
      </Box>

      {state.kind === 'loading' && <LoadingRow label="Loading shops..." />}

      {state.kind === 'error' && <LoadErrorAlert message={state.message} />}

      {state.kind === 'ready' && (
        <Paper sx={{ overflow: 'hidden' }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  {!compact && <TableCell>Type</TableCell>}
                  <TableCell>Status</TableCell>
                  {!compact && <TableCell>Owners</TableCell>}
                  {!compact && <TableCell>Last licence expiry</TableCell>}
                  <TableCell>Last sync</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shops.length === 0 && <EmptyRow colSpan={colSpan} message="No shops yet." />}
                {shops.length > 0 && visibleShops.length === 0 && (
                  <EmptyRow colSpan={colSpan} message="No shops match these filters." />
                )}
                {visibleShops.map((shop) => {
                  const archived = shop.archived_at !== null;
                  return (
                    <TableRow
                      key={shop.id}
                      hover
                      onClick={() => navigate(shopDetailPath(shop.id))}
                      sx={{ cursor: 'pointer', opacity: archived ? 0.55 : 1 }}
                    >
                      <TableCell sx={{ fontFamily: 'monospace' }}>{shop.shop_code}</TableCell>
                      <TableCell>
                        {shop.name}
                        {archived && <Chip label="Archived" size="small" sx={{ ml: 1 }} />}
                      </TableCell>
                      {!compact && <TableCell sx={{ textTransform: 'capitalize' }}>{shop.shop_type}</TableCell>}
                      <TableCell>
                        <Chip label={statusLabel(shop.status)} color={statusChipColor(shop.status)} size="small" />
                      </TableCell>
                      {!compact && <TableCell>{ownersDisplayText(shop.owners)}</TableCell>}
                      {!compact && <TableCell>{shop.last_licence_expiry ?? '-'}</TableCell>}
                      <TableCell>{shop.last_sync_at ? formatLocalDateTime(shop.last_sync_at) : 'never'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {canWrite && <ShopFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />}
    </Box>
  );
}

export default ShopsPage;
